import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProperty } from '../context/PropertyContext';
import PropertyForm from '../components/admin/PropertyForm';
import PropertyList from '../components/admin/PropertyList';
import ProjectForm from '../components/admin/ProjectForm';
import ProjectList from '../components/admin/ProjectList';
import AdminStats from '../components/admin/AdminStats';
import AdminAuth from '../components/AdminAuth';
import ThemeCustomizer from '../components/admin/ThemeCustomizer';
import GalleryManager from '../components/admin/GalleryManager';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import '../styles/admin.css';

const Admin = () => {
  const { properties, projects, deleteProperty, deleteProject } = useProperty();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [editingProperty, setEditingProperty] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);

  // Load gallery images from localStorage
  useEffect(() => {
    const loadGalleryImages = () => {
      const savedGallery = localStorage.getItem('truehome_gallery');
      if (savedGallery) {
        try {
          const gallery = JSON.parse(savedGallery);
          setGalleryImages(gallery);
        } catch (error) {
          console.error('Error loading gallery:', error);
          setGalleryImages([]);
        }
      }
    };
    loadGalleryImages();
    
    // Listen for gallery updates
    const handleStorageChange = (e) => {
      if (e.key === 'truehome_gallery') {
        loadGalleryImages();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('truehome_token');
      const adminAuth = localStorage.getItem('truehome_admin_auth');
      const userStr = localStorage.getItem('truehome_user');
      
      if (token && adminAuth && userStr) {
        try {
          const userData = JSON.parse(userStr);
          setIsAuthenticated(true);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing user data:', error);
          setIsAuthenticated(false);
          localStorage.removeItem('truehome_token');
          localStorage.removeItem('truehome_admin_auth');
          localStorage.removeItem('truehome_user');
        }
      } else {
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('truehome_token');
    localStorage.removeItem('truehome_admin_auth');
    localStorage.removeItem('truehome_user');
    setIsAuthenticated(false);
    setUser(null);
    toast.success('Logged out successfully');
  };

  const handleDeleteProperty = (id) => {
    if (window.confirm('Are you sure you want to delete this property?')) {
      deleteProperty(id);
      toast.success('Property deleted successfully');
    }
  };

  const handleDeleteProject = (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      deleteProject(id);
      toast.success('Project deleted successfully');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty(property);
    setActiveTab('add-property');
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setActiveTab('add-project');
  };

  const handlePropertyFormSuccess = () => {
    setEditingProperty(null);
    setActiveTab('properties');
  };

  const handleProjectFormSuccess = () => {
    setEditingProject(null);
    setActiveTab('projects');
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fas fa-chart-pie' },
    { id: 'properties', label: 'Properties', icon: 'fas fa-home' },
    { id: 'add-property', label: 'Add Property', icon: 'fas fa-plus' },
    { id: 'projects', label: 'Projects', icon: 'fas fa-building' },
    { id: 'add-project', label: 'Add Project', icon: 'fas fa-plus-circle' },
    { id: 'gallery', label: 'Gallery', icon: 'fas fa-images' },
    { id: 'theme', label: 'Theme', icon: 'fas fa-palette' },
    { id: 'analytics', label: 'Analytics', icon: 'fas fa-chart-bar' }
  ];

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminAuth onLogin={handleLogin} />;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-nav">
          <div className="admin-logo">
            <Link to="/">
              <h2>TrueHome<span className="accent">Admin</span></h2>
            </Link>
          </div>
          <div className="admin-nav-actions">
            {user && (
              <div className="admin-user-info" style={{ marginRight: '20px', color: '#4b5563' }}>
                <i className="fas fa-user-circle" style={{ marginRight: '8px' }}></i>
                <span>{user.username}</span>
              </div>
            )}
            <Link to="/" className="back-to-site">
              <i className="fas fa-arrow-left"></i>
              Back to Site
            </Link>
            <button className="logout-btn" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-container">
        <div className="admin-sidebar">
          <nav className="admin-menu">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`admin-menu-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="admin-content">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'dashboard' && (
              <div className="admin-dashboard">
                <div className="dashboard-header">
                  <h1>Dashboard</h1>
                  <p>Manage your luxury real estate portfolio and projects</p>
                </div>
                <AdminStats 
                  properties={properties} 
                  projects={projects}
                  onNavigate={setActiveTab}
                />
              </div>
            )}

            {activeTab === 'properties' && (
              <div className="admin-properties">
                <div className="properties-header">
                  <h1>Properties Management</h1>
                  <button
                    className="btn btn-primary"
                    onClick={() => setActiveTab('add-property')}
                  >
                    <i className="fas fa-plus"></i>
                    Add New Property
                  </button>
                </div>
                <PropertyList
                  properties={properties}
                  onEdit={handleEditProperty}
                  onDelete={handleDeleteProperty}
                />
              </div>
            )}

            {activeTab === 'add-property' && (
              <div className="admin-add-property">
                <div className="form-header">
                  <h1>{editingProperty ? 'Edit Property' : 'Add New Property'}</h1>
                  <p>Fill in the details to {editingProperty ? 'update' : 'add'} a luxury property</p>
                </div>
                <PropertyForm
                  property={editingProperty}
                  onSuccess={handlePropertyFormSuccess}
                  onCancel={() => {
                    setEditingProperty(null);
                    setActiveTab('properties');
                  }}
                />
              </div>
            )}

            {activeTab === 'projects' && (
              <div className="admin-projects">
                <div className="properties-header">
                  <h1>Projects Management</h1>
                  <button
                    className="btn btn-primary"
                    onClick={() => setActiveTab('add-project')}
                  >
                    <i className="fas fa-plus-circle"></i>
                    Add New Project
                  </button>
                </div>
                <ProjectList
                  projects={projects}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteProject}
                />
              </div>
            )}

            {activeTab === 'add-project' && (
              <div className="admin-add-project">
                <div className="form-header">
                  <h1>{editingProject ? 'Edit Project' : 'Add New Project'}</h1>
                  <p>Fill in the details to {editingProject ? 'update' : 'add'} a new project</p>
                </div>
                <ProjectForm
                  project={editingProject}
                  onSuccess={handleProjectFormSuccess}
                  onCancel={() => {
                    setEditingProject(null);
                    setActiveTab('projects');
                  }}
                />
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="admin-analytics">
                <div className="analytics-header">
                  <h1>Analytics & Reports</h1>
                  <p>Detailed insights into your property portfolio and projects</p>
                </div>
                <AdminStats 
                  properties={properties} 
                  projects={projects}
                  onNavigate={setActiveTab}
                />
              </div>
            )}

            {activeTab === 'gallery' && (
              <div className="admin-gallery">
                <GalleryManager />
              </div>
            )}

            {activeTab === 'theme' && (
              <div className="admin-theme">
                <ThemeCustomizer />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Admin;